package org.multiplatform.kotfolio.pages.kmm

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.core.Page

@Page("/kmm/key_storage")
@Composable
fun KeyStorageKMPPage() {
    BlogLayout(
        PageProperties(
            "Key Storage",
            ""
        ),
        BlogProperties("KMP", "Key Storage")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {

            //https://github.com/toby-gamez/TKOlympApp.git
            KotlinCode(
                code = """
                    private val prefsName = ""
                    private val prefs by lazy { context.getSharedPreferences(prefsName, Context.MODE_PRIVATE) }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //key.kt
                    //Name (Key Name) - serializer (Dynamic likes String, Boolean, Integer, Double, Float, Flow)
                    class Key<T> internal constructor(
                        val name: String,
                        val serializer: ValueSerializer<T>
                    )
                    
                    //ValueSerializer.kt
                    interface ValueSerializer<T> {
                        fun serialize(value: T): PrimitiveValue

                        /**
                         * Implementations should return null when the primitive cannot be
                         * converted into [T] (e.g. corrupted data or incompatible schema).
                         */
                        fun deserialize(value: PrimitiveValue?): T?
                    }
                    
                    //PrimitiveValue.kt
                    sealed class PrimitiveValue {
                        data class BooleanValue(val value: Boolean) : PrimitiveValue()
                        data class IntValue(val value: Int) : PrimitiveValue()
                        data class LongValue(val value: Long) : PrimitiveValue()
                        data class DoubleValue(val value: Double) : PrimitiveValue()
                        data class StringValue(val value: String) : PrimitiveValue()

                        /**
                         * Represents an explicit "no value" – useful to distinguish
                         * "key not present" vs. "key present with null" at the abstraction layer.
                         */
                        object NullValue : PrimitiveValue()
                    }
                    
                    //ValueSerializer.kt
                    object BuiltInSerializers {
                        val BooleanSerializer: ValueSerializer<Boolean> =
                            object : ValueSerializer<Boolean> {
                                override fun serialize(value: Boolean): PrimitiveValue =
                                    PrimitiveValue.BooleanValue(value)

                                override fun deserialize(value: PrimitiveValue?): Boolean? =
                                    (value as? PrimitiveValue.BooleanValue)?.value
                            }

                        val IntSerializer: ValueSerializer<Int> =
                            object : ValueSerializer<Int> {
                                override fun serialize(value: Int): PrimitiveValue =
                                    PrimitiveValue.IntValue(value)

                                override fun deserialize(value: PrimitiveValue?): Int? =
                                    (value as? PrimitiveValue.IntValue)?.value
                            }

                        val LongSerializer: ValueSerializer<Long> =
                            object : ValueSerializer<Long> {
                                override fun serialize(value: Long): PrimitiveValue =
                                    PrimitiveValue.LongValue(value)

                                override fun deserialize(value: PrimitiveValue?): Long? =
                                    when (value) {
                                        is PrimitiveValue.LongValue -> value.value
                                        is PrimitiveValue.IntValue -> value.value.toLong()
                                        else -> null
                                    }
                            }

                        val DoubleSerializer: ValueSerializer<Double> =
                            object : ValueSerializer<Double> {
                                override fun serialize(value: Double): PrimitiveValue =
                                    PrimitiveValue.DoubleValue(value)

                                override fun deserialize(value: PrimitiveValue?): Double? =
                                    when (value) {
                                        is PrimitiveValue.DoubleValue -> value.value
                                        is PrimitiveValue.IntValue -> value.value.toDouble()
                                        is PrimitiveValue.LongValue -> value.value.toDouble()
                                        else -> null
                                    }
                            }

                        val StringSerializer: ValueSerializer<String> =
                            object : ValueSerializer<String> {
                                override fun serialize(value: String): PrimitiveValue =
                                    PrimitiveValue.StringValue(value)

                                override fun deserialize(value: PrimitiveValue?): String? =
                                    (value as? PrimitiveValue.StringValue)?.value
                            }
                    }
                    
                    //KeyValueStore.kt
                    interface KeyValueStore {

                        /**
                         * Store [value] under [key]. Passing null removes the key.
                         */
                        suspend fun <T> put(key: Key<T>, value: T?)

                        /**
                         * Read the current value for [key], or null if it doesn't exist or
                         * cannot be deserialized.
                         */
                        suspend fun <T> get(key: Key<T>): T?

                        /**
                         * Read the current value for [key], or return [default] when the key
                         * is missing or deserialization fails.
                         */
                        suspend fun <T> getOrDefault(key: Key<T>, default: T): T =
                            get(key) ?: default

                        /**
                         * Returns true when [key] currently exists in the store.
                         */
                        suspend fun contains(key: Key<*>): Boolean

                        /**
                         * Remove [key] from the store if it exists.
                         */
                        suspend fun remove(key: Key<*>)

                        /**
                         * Remove all keys and values from this store.
                         */
                        suspend fun clear()

                        /**
                         * Observe changes to [key] as a hot stream.
                         *
                         * Implementations should emit the current value immediately when
                         * collection starts, and then emit new values whenever [put] is
                         * called for this key (or the underlying storage changes).
                         */
                        fun <T> observe(key: Key<T>): Flow<T?>

                        /**
                         * Convenience wrapper around [observe] that maps nulls to a [default]
                         * value, so callers don't need to deal with nullable types.
                         */
                        fun <T> observeOrDefault(key: Key<T>, default: T): Flow<T> =
                            observe(key).map { it ?: default }
                    }
                    
                    //KeyStore.kt
                    /**
                     * Entry point used from common code. This delegates to an `expect`/`actual`
                     * function that creates the appropriate platform-specific implementation.
                     */
                    object KeyStore {

                        private const val DEFAULT_STORE_NAME = "default"

                        /**
                         * Get a store for general application data.
                         */
                        fun default(config: DatastoreConfig = DatastoreConfig()): KeyValueStore =
                            createPlatformStore(DEFAULT_STORE_NAME, config)

                        /**
                         * Get (or create) a named store. Platforms are free to map this
                         * to separate files, suites, or namespaces.
                         */
                        fun named(name: String, config: DatastoreConfig = DatastoreConfig()): KeyValueStore =
                            createPlatformStore(name, config)
                    }

                    internal expect fun createPlatformStore(
                        name: String,
                        config: DatastoreConfig
                    ): KeyValueStore
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //androidMain
                    
                    class AndroidKeyStore constructor(
                        private val dataStore: DataStore<Preferences>
                    ): KeyValueStore {
                        override suspend fun <T> put(key: Key<T>, value: T?) {
                            dataStore.edit { prefs ->
                                if (value == null) {
                                    removeAllRepresentations(prefs, key.name)
                                } else {
                                    val primitive = key.serializer.serialize(value)
                                    putPrimitive(prefs, key.name, primitive)
                                }
                            }
                        }

                        override suspend fun <T> get(key: Key<T>): T? {
                            val prefs = dataStore.data.first()
                            val primitive = readPrimitive(prefs, key.name, key.serializer)
                            return key.serializer.deserialize(primitive)
                        }

                        override suspend fun contains(key: Key<*>): Boolean {
                            val prefs = dataStore.data.first()
                            // We only check for presence of *any* representation.
                            return prefs[stringPreferencesKey(key.name)] != null ||
                                    prefs[booleanPreferencesKey(key.name)] != null ||
                                    prefs[intPreferencesKey(key.name)] != null ||
                                    prefs[longPreferencesKey(key.name)] != null ||
                                    prefs[doublePreferencesKey(key.name)] != null
                        }

                        override suspend fun remove(key: Key<*>) {
                            dataStore.edit { prefs ->
                                removeAllRepresentations(prefs, key.name)
                            }
                        }

                        override suspend fun clear() {
                            dataStore.edit { prefs ->
                                prefs.clear()
                            }
                        }

                        override fun <T> observe(key: Key<T>): Flow<T?> {
                            return dataStore.data.map { prefs ->
                                val primitive = readPrimitive(prefs, key.name, key.serializer)
                                key.serializer.deserialize(primitive)
                            }
                        }

                        private fun putPrimitive(
                            prefs: MutablePreferences,
                            name: String,
                            primitive: PrimitiveValue
                        ) {
                            when (primitive) {
                                is PrimitiveValue.BooleanValue ->
                                    prefs[booleanPreferencesKey(name)] = primitive.value
                                is PrimitiveValue.IntValue ->
                                    prefs[intPreferencesKey(name)] = primitive.value
                                is PrimitiveValue.LongValue ->
                                    prefs[longPreferencesKey(name)] = primitive.value
                                is PrimitiveValue.DoubleValue ->
                                    prefs[doublePreferencesKey(name)] = primitive.value
                                is PrimitiveValue.StringValue ->
                                    prefs[stringPreferencesKey(name)] = primitive.value
                                PrimitiveValue.NullValue -> removeAllRepresentations(prefs, name)
                            }
                        }

                        private fun removeAllRepresentations(
                            prefs: MutablePreferences,
                            name: String
                        ) {
                            prefs.remove(stringPreferencesKey(name))
                            prefs.remove(booleanPreferencesKey(name))
                            prefs.remove(intPreferencesKey(name))
                            prefs.remove(longPreferencesKey(name))
                            prefs.remove(doublePreferencesKey(name))
                        }

                        private fun <T> readPrimitive(
                            prefs: Preferences,
                            name: String,
                            serializer: ValueSerializer<T>
                        ): PrimitiveValue? {
                            return when (serializer) {
                                BuiltInSerializers.BooleanSerializer -> {
                                    prefs[booleanPreferencesKey(name)]?.let { PrimitiveValue.BooleanValue(it) }
                                }

                                BuiltInSerializers.IntSerializer -> {
                                    prefs[intPreferencesKey(name)]?.let { PrimitiveValue.IntValue(it) }
                                }

                                BuiltInSerializers.LongSerializer -> {
                                    prefs[longPreferencesKey(name)]?.let { PrimitiveValue.LongValue(it) }
                                        ?: prefs[intPreferencesKey(name)]?.let { PrimitiveValue.LongValue(it.toLong()) }
                                }

                                BuiltInSerializers.DoubleSerializer -> {
                                    prefs[doublePreferencesKey(name)]?.let { PrimitiveValue.DoubleValue(it) }
                                        ?: prefs[intPreferencesKey(name)]?.let { PrimitiveValue.DoubleValue(it.toDouble()) }
                                        ?: prefs[longPreferencesKey(name)]?.let { PrimitiveValue.DoubleValue(it.toDouble()) }
                                }

                                BuiltInSerializers.StringSerializer -> {
                                    prefs[stringPreferencesKey(name)]?.let { PrimitiveValue.StringValue(it) }
                                }

                                else -> {
                                    // All custom serializers (e.g. JSON) store as String.
                                    prefs[stringPreferencesKey(name)]?.let { PrimitiveValue.StringValue(it) }
                                }
                            }
                        }
                    }

                    object AndroidDatastoreInitializer {
                        @Volatile
                        private var applicationContext: Context? = null

                        fun init(context: Context) {
                            applicationContext = context.applicationContext
                        }

                        internal fun requireContext(): Context =
                            applicationContext ?: error(
                                "AndroidDatastoreInitializer.init(context) must be called before using Datastore.default()"
                            )
                    }

                    private val dataStores = mutableMapOf<String, DataStore<Preferences>>()

                    internal actual fun createPlatformStore(
                        name: String,
                        config: DatastoreConfig
                    ): KeyValueStore {
                        val context = AndroidDatastoreInitializer.requireContext()
                        val dataStore = synchronized(dataStores) {
                            dataStores.getOrPut(name) {
                                PreferenceDataStoreFactory.create(
                                    corruptionHandler = ReplaceFileCorruptionHandler { emptyPreferences() },
                                    produceFile = { context.preferencesDataStoreFile(name) }
                                )
                            }
                        }
                        return AndroidKeyStore(dataStore)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //iosMain
                    import kotlinx.coroutines.flow.Flow
                    import kotlinx.coroutines.flow.MutableStateFlow
                    import kotlinx.coroutines.flow.map
                    import platform.Foundation.NSNumber
                    import platform.Foundation.NSString
                    import platform.Foundation.NSUserDefaults

                    class IOSKeyStore(
                        private val defaults: NSUserDefaults
                    ): KeyValueStore {
                        private val primitiveFlows = mutableMapOf<String, MutableStateFlow<PrimitiveValue?>>()

                        override suspend fun <T> put(key: Key<T>, value: T?) {
                            if (value == null) {
                                remove(key)
                                return
                            }

                            val primitive = key.serializer.serialize(value)
                            writePrimitive(key.name, primitive)
                            primitiveFlows.getOrPut(key.name) {
                                MutableStateFlow(readPrimitive(key.name, key.serializer))
                            }.value = primitive
                        }

                        override suspend fun <T> get(key: Key<T>): T? {
                            val primitive = readPrimitive(key.name, key.serializer)
                            return key.serializer.deserialize(primitive)
                        }

                        override suspend fun contains(key: Key<*>): Boolean {
                            return defaults.objectForKey(key.name) != null
                        }

                        override suspend fun remove(key: Key<*>) {
                            defaults.removeObjectForKey(key.name)
                            primitiveFlows[key.name]?.value = null
                        }

                        override suspend fun clear() {
                            // Not clearing the whole user defaults domain to avoid wiping
                            // unrelated keys. For simplicity we only clear keys we know about.
                            primitiveFlows.keys.forEach { name ->
                                defaults.removeObjectForKey(name)
                            }
                            primitiveFlows.values.forEach { it.value = null }
                        }

                        override fun <T> observe(key: Key<T>): Flow<T?> {
                            val flow = primitiveFlows.getOrPut(key.name) {
                                MutableStateFlow(readPrimitive(key.name, key.serializer))
                            }
                            return flow.map { primitive ->
                                key.serializer.deserialize(primitive)
                            }
                        }

                        // region helpers

                        private fun writePrimitive(
                            name: String,
                            primitive: PrimitiveValue
                        ) {
                            when (primitive) {
                                is PrimitiveValue.BooleanValue ->
                                    defaults.setBool(primitive.value, forKey = name)
                                is PrimitiveValue.IntValue ->
                                    defaults.setInteger(primitive.value.toLong(), forKey = name)
                                is PrimitiveValue.LongValue ->
                                    defaults.setInteger(primitive.value, forKey = name)
                                is PrimitiveValue.DoubleValue ->
                                    defaults.setDouble(primitive.value, forKey = name)
                                is PrimitiveValue.StringValue ->
                                    defaults.setObject(primitive.value, forKey = name)
                                PrimitiveValue.NullValue ->
                                    defaults.removeObjectForKey(name)
                            }
                        }

                        private fun <T> readPrimitive(
                            name: String,
                            serializer: ValueSerializer<T>
                        ): PrimitiveValue? {
                            val any = defaults.objectForKey(name) ?: return null

                            return when (serializer) {
                                BuiltInSerializers.BooleanSerializer -> {
                                    (any as? NSNumber)?.boolValue()?.let { PrimitiveValue.BooleanValue(it) }
                                }

                                BuiltInSerializers.IntSerializer -> {
                                    (any as? NSNumber)?.intValue?.let { PrimitiveValue.IntValue(it) }
                                }

                                BuiltInSerializers.LongSerializer -> {
                                    (any as? NSNumber)?.longLongValue?.let { PrimitiveValue.LongValue(it) }
                                }

                                BuiltInSerializers.DoubleSerializer -> {
                                    (any as? NSNumber)?.doubleValue?.let { PrimitiveValue.DoubleValue(it) }
                                }

                                BuiltInSerializers.StringSerializer -> {
                                    (any as? NSString)?.toString()?.let { PrimitiveValue.StringValue(it) }
                                }

                                else -> {
                                    // Custom / JSON serializers use String underneath.
                                    (any as? NSString)?.toString()?.let { PrimitiveValue.StringValue(it) }
                                }
                            }
                        }
                    }

                    internal actual fun createPlatformStore(
                        name: String,
                        config: DatastoreConfig
                    ): KeyValueStore {
                        val defaults = NSUserDefaults.standardUserDefaults()
                        return IOSKeyStore(defaults)
                    }
                    """.trimIndent()
            )
        }
    }
}